// Pixar Whiteboard Ad's storyboard stage (live's `storyboard-stage` and `whiteboard-still-card`; not
// captured, live's stage needs a paid Approve script, so built from live's code, spec
// G14-pixar-whiteboard-ad PR g2): "Storyboard", the eight images in two rows each with its ↻, and
// the cue warnings, never blocking. Live has no "Back to script" here.
import { Button, Group, Stack, Text } from "@mantine/core";
import { useState } from "react";

import type { PixarWhiteboardDraftStill, PixarWhiteboardStillName } from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { StillCard } from "./StillCard";
import { STILL_NAMES, stillWaiting } from "./pixar-whiteboard-draft";
import classes from "./podcast.module.css";
import whiteboard from "./whiteboard.module.css";

/** Live's words (`constants.ts`). */
export const STILL_LABELS: Readonly<Record<PixarWhiteboardStillName, string>> = {
  sheet: "Character sheet",
  master_board: "Master board",
  char_ref: "Character reference",
  opening: "Opening",
  anchor_a: "Anchor A",
  anchor_b: "Anchor B",
  anchor_c: "Anchor C",
  end_card: "End card",
};
const STORYBOARD_TITLE = "Storyboard";
const START_OVER = "Start over";
const START_OVER_CONFIRM = "The script, the board and the eight images are cleared, and the form starts empty.";
/** Live's two rows: the sheet, the board and the reference above, the frames and the end card below. */
const STILLS_PER_ROW = 4;
const ROWS = [STILL_NAMES.slice(0, STILLS_PER_ROW), STILL_NAMES.slice(STILLS_PER_ROW)];
const PENDING = { status: "pending", url: null, problem: null } as const;

function stillItem(still: PixarWhiteboardDraftStill & { url: string }): PreviewItem {
  return {
    id: still.name,
    name: STILL_LABELS[still.name],
    kind: "image",
    url: still.url,
    thumb: still.url,
    poster: null,
    durationMs: null,
    width: null,
    height: null,
  };
}

export interface WhiteboardStoryboardProps {
  stills: readonly PixarWhiteboardDraftStill[];
  cueWarnings: readonly string[];
  /** The board director plans: every image waits on it (live's `isBoardDirecting`). */
  boarding: boolean;
  /** Nothing is drawn again: the draft works, or the takes are drawn between these stills. */
  locked: boolean;
  /** "Start over" is off: the draft works, or asks. */
  busy: boolean;
  resetting: boolean;
  onRegenerate: (name: PixarWhiteboardStillName) => void;
  onStartOver: () => void;
}

/**
 * Live's storyboard: its title, the eight images in live's two rows, the sheet wide and a done one
 * opening in the shared lightbox, then the cue warnings. Ours also offers "Start over", as Chalk
 * Explainer Ad's (the draft outlives the panel).
 */
export function WhiteboardStoryboard(props: WhiteboardStoryboardProps) {
  const { stills, cueWarnings, boarding, locked, busy, resetting, onRegenerate, onStartOver } = props;
  const [preview, setPreview] = useState<PixarWhiteboardStillName | null>(null);
  const [confirming, setConfirming] = useState(false);
  const made = stills.flatMap((still) => (still.url ? [{ ...still, url: still.url }] : []));
  return (
    <Stack gap={12} aria-label={STORYBOARD_TITLE} component="section">
      <Group justify="space-between" wrap="nowrap">
        <Text className={classes.castTitle}>{STORYBOARD_TITLE}</Text>
        <Button variant="subtle" size="compact-sm" disabled={resetting || busy} onClick={() => setConfirming(true)}>
          {START_OVER}
        </Button>
      </Group>
      {ROWS.map((row) => (
        <div key={row.join()} className={`${classes.stills} ${whiteboard.stillRow}`}>
          {row.map((name) => (
            <StillCard
              key={name}
              label={STILL_LABELS[name]}
              still={stills.find((each) => each.name === name) ?? PENDING}
              waiting={boarding || stillWaiting(name, stills)}
              busy={locked}
              wide={name === "sheet"}
              onRegenerate={() => onRegenerate(name)}
              onFixLabel={null}
              onPreview={() => setPreview(name)}
            />
          ))}
        </div>
      ))}
      {cueWarnings.map((warning) => (
        <Text key={warning} className={whiteboard.cueWarning}>
          {warning}
        </Text>
      ))}
      <MediaPreview
        items={made.map(stillItem)}
        current={preview}
        onShow={(name) => setPreview(name as PixarWhiteboardStillName | null)}
      />
      <ConfirmModal
        opened={confirming}
        title={START_OVER}
        confirmLabel={START_OVER}
        pending={resetting}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          onStartOver();
          setConfirming(false);
        }}
      >
        <Text size="sm">{START_OVER_CONFIRM}</Text>
      </ConfirmModal>
    </Stack>
  );
}
